import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Button,
  MenuItem,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";

import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as rToolTip,
  Cell,
} from "recharts";

export default function OpsActivityLogsPage() {
  const pageTitle = "Activity Logs";
  usePageTitle(pageTitle);
  const { ref, filterType, filterRef } = useParams();
  const navigate = useNavigate();

  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [logs, setLogs] = useState([]);

  const [days, setDays] = useState(60);
  const [overview, setOverview] = useState(null);

  const logsTableColumns = [
    { Header: "Actions", accessor: "actions", align: "left" },
    { Header: "Activity", accessor: "activity", align: "left" },
    { Header: "Entity", accessor: "entity", align: "left" },
    { Header: "Agent", accessor: "agent", align: "left" },
    { Header: "Entity Group", accessor: "group", align: "left" },
    { Header: "State", accessor: "state", align: "left" },
    { Header: "LGA", accessor: "lga", align: "left" },
    { Header: "Date", accessor: "created", align: "right" },
  ];

  const buildRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        activity: row.activity.title,
        entity: row.entity.title,
        agent: row.agent.title,
        group: row.entity.group,
        state: row.state,
        lga: row.lga,
        actions: (
          <MDBox mt={1} variant="button" display="flex" gap={0.5}>
            <Tooltip title="View Activity Log">
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                iconOnly
                onClick={() => {
                  navigate(`/ops/activities/logs/${row.reference}`);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small">
                  visibility
                </Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      let queryParams = {
        days: days,
      };

      if (ref) {
        queryParams["reference"] = ref;
      }

      if (filterType && filterRef) {
        queryParams[filterType] = filterRef;
      }

      console.log(queryParams);

      await axios
        .get(`/portal/ops/activities/logs.json`, { params: queryParams })
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            console.log(result.data.data);
            setOverview(result.data.data.overview);
            const rows = buildRows(result.data.data.logs);
            setLogs(rows);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage, days]);

  const COLORS = ["#1976d2", "#43a047", "#fb8c00", "#8e24aa", "#e53935", "#00897b", "#3949ab"];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <MDBox pb={3}>
        {pageLoaded && (
          <MDBox>
            <MDBox mt={1} mb={1.5}>
              <Button
                color="dark"
                startIcon={<Icon>west</Icon>}
                onClick={() => {
                  navigate(`/ops/activities`);
                }}
              >
                Back to Activities
              </Button>
            </MDBox>
            {overview && (
              <MDBox mb={5}>
                <Card>
                  <MDBox p={2}>
                    <MDBox mb={3} display="flex" justifyContent="space-between">
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="bold">
                          Activity Overview
                        </MDTypography>
                        <MDTypography variant="h6" fontWeight="light">
                          Team's activity overview
                        </MDTypography>
                      </MDBox>
                      <MDBox pt={0.5}>
                        <MDInput
                          select
                          label="Date Range"
                          onChange={(e) => {
                            setDays(parseInt(e.target.value, 10));
                          }}
                          fullWidth
                          SelectProps={{ style: { padding: "12px 0px" } }}
                          sx={{ minWidth: "120px" }}
                          value={days}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        >
                          <MenuItem value={1}>Today</MenuItem>
                          <MenuItem value={2}>Yesterday</MenuItem>
                          <MenuItem value={7}>Last 7 days</MenuItem>
                          <MenuItem value={14}>Last 14 days</MenuItem>
                          <MenuItem value={30}>Last 30 days</MenuItem>
                        </MDInput>
                      </MDBox>
                    </MDBox>
                    <MDBox mt={1}>
                      <ResponsiveContainer width="100%" height={320}>
                        <BarChart
                          data={overview}
                          layout="vertical"
                          margin={{
                            top: 10,
                            right: 20,
                            left: 30,
                            bottom: 10,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" allowDecimals={false} />
                          <YAxis type="category" dataKey="label" width={140} />
                          <Tooltip />
                          <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={18}>
                            {overview.map((entry, index) => (
                              <Cell key={entry.label} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            )}
            <MDBox mb={3}>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox>
                  <MDBox p={2}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                          Activity Logs
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                  <MDBox mt={0}>
                    <DataTable
                      table={{ columns: logsTableColumns, rows: logs }}
                      isSorted
                      canSearch
                      entriesPerPage
                      showTotalEntries
                      buttons
                      noEndBorder
                    />
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
